import { forwardRef, useEffect, useState } from 'react';
import type { PropsWithoutRef, Ref, KeyboardEvent as ReactKeyboardEvent } from 'react';
import styled, { css } from 'styled-components';

import Icon, { registerIcon } from '../Icon';
import * as openIcon from '../Icon/icons/open.icon';
import * as timesIcon from '../Icon/icons/times.icon';
import * as previewIcon from '../Icon/icons/preview.icon';
import * as editIcon from '../Icon/icons/pencil.icon';
import * as downloadIcon from '../Icon/icons/download.icon';
import * as deleteIcon from '../Icon/icons/trash.icon';
import Text, { StyledText } from '../Text';
import Tooltip from '../Tooltip';
import type { ForwardRefForwardPropsComponent, BaseProps, Action } from '../../types';
import { defaultThemeProp } from '../../theme';
import Button, { StyledButton } from '../Button';
import {
  useI18n,
  useElement,
  useConfiguration,
  useConsolidatedRef,
  usePreviewContext,
  useModalContext
} from '../../hooks';
import Actions from '../Actions';
import Progress from '../Progress';
import { StyledProgressRing } from '../Progress/Progress.styles';
import { formatNumber } from '../Number';
import Flex from '../Flex';
import { StyledIconShape } from '../Icon/Icon';
import { getActiveElement, getFocusables } from '../../utils';
import { ellipsisOverflow } from '../../styles/mixins';
import { StyledLinkPopover, StyledLinkPreviewPopoverBtn } from '../Link/Link';
import Popover from '../Popover';
import { Keyboard } from '../Badges';

import { getKindFromMimeType, getMimeTypeFromFile } from './utils';
import FileVisual, { StyledFileVisual } from './FileVisual';
import type { FileVisualProps } from './FileVisual';

registerIcon(openIcon, timesIcon, previewIcon, editIcon, downloadIcon, deleteIcon);

export interface FileItemProps extends BaseProps {
  /** Unique id for item. */
  id: string;
  /** Name of the file. */
  name: string;
  /** A string to be used as an image src for a attachment thumbnail. Falls back to a provided icon or the default attachment icon. */
  thumbnail?: FileVisualProps['thumbnail'];
  /** File extension. Use 'url' for link */
  format?: string;
  /**
   * Used for a smaller sized FileItem.
   * @default false
   */
  compact?: boolean;
  /** When passed, will render a single icon button for yet to be or in progress uploads. Use this action to trigger xhr request termination. */
  onCancel?: (id: FileItemProps['id']) => void;
  /** When passed, will render a single icon button or within a MenuButton if other actions are defined. */
  onEdit?: (id: FileItemProps['id']) => void;
  /** When passed, the file name will be rendered as a clickable button. Fires on direct click of the name. Passes file id as the parameter. */
  onPreview?: (id: FileItemProps['id']) => void;
  /** When passed, will render a preview tooltip. Will be fired on click of preview tooltip. Passes file id as the parameter. */
  onPreviewPopoverClick?: (id: FileItemProps['id']) => void;
  /**
   * Moves preview option to actions menu.
   * To be used only in compact mode of single file input when rendered in a table
   */
  previewFromActions?: boolean;
  /** When passed, will render a single icon button or within a MenuButton if other actions are defined. Passes file id as the parameter. */
  onDownload?: (id: FileItemProps['id']) => void;
  /** When passed, will render a single icon button or within a MenuButton if other actions are defined. */
  onDelete?: (id: FileItemProps['id']) => void;
  /** When passed, will render a single icon button or within a MenuButton if other actions are defined. */
  onOpen?: (id: FileItemProps['id']) => void;
  /** If the attachment is in an upload state, pass progress as a number between 0-100 representing the percentage of progress. */
  progress?: number;
  /** Set if there is an error uploading the file. Applies error styling to the meta string passed. */
  error?: string;
  /** Setting the disable state */
  disabled?: boolean;
  /** Ref for the wrapping element. */
  ref?: Ref<HTMLDivElement>;
}

const StyledErrorIcon = styled(Icon)(({ theme }) => {
  return css`
    color: ${theme.base.palette.urgent};
    margin-inline-start: calc(0.5 * ${theme.base.spacing});
  `;
});

StyledErrorIcon.defaultProps = defaultThemeProp;

const titleEllipsisStyle = css`
  flex-shrink: 1;
  ${ellipsisOverflow}
`;

export const StyledPreviewTitle = styled(Button)`
  ${titleEllipsisStyle}
`;

export const StyledTextTitle = styled(Text)`
  ${titleEllipsisStyle}
`;

const StyledMeta = styled(StyledText)<{ error: boolean }>(({ theme, error }) => {
  return css`
    /* min width to show percentage in full */
    min-width: 4ch;
    margin-inline-start: auto;
    ${titleEllipsisStyle}
    ${error &&
    css`
      color: ${theme.base.palette.urgent};
    `}
  `;
});

StyledMeta.defaultProps = defaultThemeProp;

const StyledActionsWrapper = styled.div<{ meta: boolean }>(({ meta }) => {
  return css`
    margin-inline-start: ${meta ? 'unset' : 'auto'};
  `;
});

StyledActionsWrapper.defaultProps = defaultThemeProp;

export const StyledFileItem = styled.div<{
  compactMode: FileItemProps['compact'];
  isDisabled?: FileItemProps['disabled'];
}>(
  ({
    theme: {
      base: {
        palette,
        spacing,
        'hit-area': { compact },
        'disabled-opacity': disabledOpacity
      },
      components: {
        input,
        'form-control': {
          ':disabled': {
            'background-color': disabledBackgroundColor,
            'border-color': disabledBorderColor
          }
        }
      }
    },
    compactMode,
    isDisabled
  }) => {
    return css`
      border-radius: calc(var(--container-border-radius) * 0.5);
      border: 0.0625rem solid ${palette['border-line']};
      background-color: ${palette['primary-background']};
      height: ${input.height};
      min-width: calc(${input.height} + 12ch + 1.5 * ${spacing} + ${compact});

      ${compactMode &&
      css`
        height: ${compact};
        min-width: calc(${compact} + 12ch + 2 * ${spacing});
        width: fit-content;
        max-width: 25ch;

        ${StyledPreviewTitle}, ${StyledTextTitle} {
          min-width: 12ch;
          max-width: calc(25ch - (${compact} + 2 * ${spacing}));
        }

        ${StyledActionsWrapper} > ${StyledButton} {
          min-height: 100%;
          min-width: 100%;
          margin-block: ${spacing};
        }
      `}

      > ${StyledProgressRing} {
        margin-inline-start: calc(0.5 * ${spacing});
      }

      > ${StyledFileVisual}, > ${StyledIconShape} {
        border-start-start-radius: inherit;
        border-end-start-radius: inherit;
        border-start-end-radius: 0;
        border-end-end-radius: 0;
        width: ${input.height};
        flex-shrink: 0;

        ${compactMode &&
        css`
          height: ${compact};
          width: ${compact};
        `}
      }
      ${isDisabled &&
      css`
        background-color: ${disabledBackgroundColor};
        border-color: ${disabledBorderColor};
        opacity: ${disabledOpacity};
      `}
    `;
  }
);

StyledFileItem.defaultProps = defaultThemeProp;

const FileItem: ForwardRefForwardPropsComponent<FileItemProps> = forwardRef(function FileItem(
  props: PropsWithoutRef<FileItemProps>,
  ref: FileItemProps['ref']
) {
  const {
    id,
    name,
    thumbnail,
    format,
    error,
    onCancel,
    onEdit,
    onPreview,
    onPreviewPopoverClick,
    onDownload,
    onDelete,
    onOpen,
    progress,
    compact = false,
    previewFromActions = false,
    disabled = false,
    ...restProps
  } = props;

  const [thumbnailError, setThumbnailError] = useState(false);
  const [textTarget, setTextTarget] = useElement();
  const [metaTarget, setMetaTarget] = useElement();
  const [popover, setPopover] = useState(false);
  const [previewPopover, setPreviewPopover] = useState(false);
  const { locale } = useConfiguration();
  const { initialized: inModal } = useModalContext();
  const { previewTriggerRef } = usePreviewContext();
  const t = useI18n();
  const uploading = typeof progress === 'number' && progress < 100;
  const itemRef = useConsolidatedRef(ref);

  const activeElement = getActiveElement();
  const activeElementInItem = itemRef.current?.contains(activeElement);

  let metaMessage;

  if (uploading) {
    metaMessage = formatNumber(progress, {
      locale,
      options: {
        style: 'unit',
        unit: 'percent',
        maximumFractionDigits: 0
      }
    });
  } else if (error) {
    metaMessage = error;
  }

  const definedActions: Action[] = [
    previewFromActions ? { onClick: onPreview, text: t('preview'), icon: 'preview' } : undefined,
    { onClick: onEdit, text: t('edit'), icon: 'pencil' },
    { onClick: onDownload, text: t('download'), icon: 'download' },
    { onClick: onDelete, text: t('delete'), icon: 'trash' },
    { onClick: onOpen, text: t('link_open_in_tab_text'), icon: 'open' }
  ].flatMap(action => {
    if (!action || !action.onClick) return [];

    const { text, icon, onClick: onActionClick } = action;
    return [
      {
        id: text,
        text,
        icon,
        onClick: () => onActionClick(id)
      }
    ];
  });

  let visual;
  let contextualLabel;

  if (uploading) {
    visual = <Progress variant='ring' placement='inline' value={progress} />;
    contextualLabel = t('uploading_file', [name]);
  } else if (error) {
    visual = <StyledErrorIcon name='warn-solid' />;
    contextualLabel = t('upload_failed', [name, error]);
  } else {
    visual = (
      <FileVisual
        type={
          format === 'url' ? 'link' : getKindFromMimeType(getMimeTypeFromFile(format ?? '') ?? '')
        }
        thumbnail={thumbnailError ? undefined : thumbnail}
        onThumbnailError={() => {
          setThumbnailError(true);
        }}
      />
    );
    contextualLabel = name;
  }

  useEffect(() => {
    if (!activeElement?.isConnected && activeElementInItem && itemRef.current) {
      getFocusables(itemRef.current)[0]?.focus();
    }
  }, [progress, error, onCancel, onDelete, onPreview, onEdit, onDownload]);

  return (
    <Flex
      as={StyledFileItem}
      container={{
        pad: [undefined, 0.5, undefined, undefined],
        alignItems: 'center',
        justify: 'start',
        gap: 1
      }}
      isDisabled={disabled}
      compactMode={compact}
      ref={itemRef}
    >
      {visual}

      {!previewFromActions &&
      ((!inModal && onPreviewPopoverClick) || onPreview) &&
      !(error || uploading) ? (
        <Button
          {...restProps}
          as={StyledPreviewTitle}
          variant='link'
          ref={setTextTarget}
          onClick={() => {
            onPreview?.(id);
          }}
          onKeyDown={(e: ReactKeyboardEvent) => {
            if (e.getModifierState('Alt') && e.code === 'KeyP') {
              previewTriggerRef.current = e.currentTarget as HTMLElement;
              onPreviewPopoverClick?.(id);
            }
          }}
          onMouseEnter={onPreviewPopoverClick ? () => setPopover(true) : undefined}
          onMouseLeave={onPreviewPopoverClick ? () => setPopover(false) : undefined}
          onFocus={onPreviewPopoverClick ? () => setPreviewPopover(true) : undefined}
          onBlur={onPreviewPopoverClick ? () => setPreviewPopover(false) : undefined}
          aria-label={t('preview_file', [name])}
          disabled={disabled}
        >
          {name}
          {format === 'url' && <Icon name='open' />}
        </Button>
      ) : (
        <Text {...restProps} as={StyledTextTitle} ref={setTextTarget}>
          {name}
        </Text>
      )}
      {metaMessage && (
        <>
          <Text as={StyledMeta} variant='secondary' error={error && !uploading} ref={setMetaTarget}>
            {metaMessage}
          </Text>

          {metaTarget && (
            <Tooltip target={metaTarget} describeTarget={false} smart>
              {metaMessage}
            </Tooltip>
          )}
        </>
      )}

      {(error || uploading) && onCancel && (
        <StyledActionsWrapper meta={!!metaMessage}>
          <Button
            label={t('cancel')}
            aria-label={`${t('cancel')} - ${contextualLabel}`}
            variant='simple'
            compact
            icon
            onClick={() => onCancel(id)}
          >
            <Icon name='times' />
          </Button>
        </StyledActionsWrapper>
      )}

      {!(error || uploading) && !!definedActions.length && (
        <StyledActionsWrapper meta={!!metaMessage}>
          <Actions
            items={definedActions}
            compact
            iconOnly
            contextualLabel={name}
            disabled={disabled}
          />
        </StyledActionsWrapper>
      )}

      {textTarget && !onPreviewPopoverClick && (
        <Tooltip target={textTarget} smart describeTarget={false}>
          {name}
        </Tooltip>
      )}

      {onPreviewPopoverClick && !inModal && !previewFromActions && (
        <Popover
          as={StyledLinkPopover}
          show={popover}
          placement='top'
          groupId='tooltip'
          showDelay='short'
          target={textTarget}
          onMouseEnter={() => setPopover(true)}
          onMouseLeave={() => setPopover(false)}
          arrow
          portal
        >
          <StyledLinkPreviewPopoverBtn
            type='button'
            onClick={() => {
              previewTriggerRef.current = textTarget as HTMLElement;
              onPreviewPopoverClick?.(id);
            }}
            tabIndex={-1}
          >
            <div>
              <span>{t('preview')}</span>
            </div>
          </StyledLinkPreviewPopoverBtn>
        </Popover>
      )}

      {onPreviewPopoverClick && !inModal && !previewFromActions && (
        <Popover
          as={StyledLinkPopover}
          show={previewPopover}
          placement='top'
          groupId='tooltip'
          showDelay='short'
          target={textTarget}
          arrow
          portal
        >
          <StyledLinkPreviewPopoverBtn
            type='button'
            onClick={() => {
              previewTriggerRef.current = textTarget as HTMLElement;
              onPreviewPopoverClick?.(id);
            }}
            tabIndex={-1}
          >
            <div>
              <span>{t('preview')}</span>
              <Flex container={{ inline: true, justify: 'end' }}>
                <Keyboard keyName='Alt' />
                <Keyboard keyName='P' />
              </Flex>
            </div>
          </StyledLinkPreviewPopoverBtn>
        </Popover>
      )}
    </Flex>
  );
});

export default FileItem;
